'use client';

import React from 'react';
import { useRouter } from 'next/navigation';
import { Hammer, Car, Home, Wrench, CalendarHeart, ChevronRight,  } from 'lucide-react';
import { TRADE_SUPER_CATEGORIES } from '@/app/admin/scraper/data/tradeCategories';

const SUPER_CATEGORY_META: Record<string, { icon: React.ElementType; color: string; bgColor: string }> = {
  construction: { icon: Hammer, color: 'text-purple-700', bgColor: 'bg-purple-50' },
  'home-lifestyle': { icon: Home, color: 'text-emerald-700', bgColor: 'bg-emerald-50' },
  'vehicle-transport': { icon: Car, color: 'text-blue-700', bgColor: 'bg-blue-50' },
  'events-hospitality': { icon: CalendarHeart, color: 'text-rose-700', bgColor: 'bg-rose-50' },
};

export default function CategoryGrid() {
  const router = useRouter();

  return (
    <section className="py-12 md:py-16 relative">
      <div className="absolute inset-0">
        <div
          className="w-full h-full"
          style={{
            backgroundImage: `url("https://lovelocals.co.uk/assets/hero-geometric-BCt4JoXz.jpg")`,
            backgroundSize: '800px',
            backgroundRepeat: 'repeat',
          }}
        />
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(248,245,250,0.95)' }} />
      </div>

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="text-center mb-8">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-gray-900 mb-2">
            Browse by Category
          </h2>
          <p className="text-gray-500">Find the right professional for your job</p>
        </div>

        <div className="space-y-6">
          {TRADE_SUPER_CATEGORIES.map((superCat) => {
            const meta = SUPER_CATEGORY_META[superCat.id] ?? { icon: Wrench, color: 'text-gray-700', bgColor: 'bg-gray-50' };
            const SuperIcon = meta.icon;
            const totalServices = superCat.categories.reduce((sum, c) => sum + c.services.length, 0);

            return (
              <div key={superCat.id}>
                {/* Super-category header */}
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">{superCat.emoji}</span>
                  <h3 className="font-display font-bold text-gray-800 text-base">{superCat.label}</h3>
                  <span className="text-xs text-gray-400 font-medium">{totalServices} services</span>
                </div>

                {/* Parent category cards */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2.5">
                  {superCat.categories.map((parentCat) => (
                    <button
                      key={parentCat.id}
                      onClick={() => router.push('/provider-directory')}
                      className="group flex flex-col items-start gap-1.5 p-3.5 rounded-xl border border-gray-200 bg-white transition-all duration-200 hover:border-gray-300 hover:shadow-sm active:scale-95 text-left"
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${meta.bgColor}`}>
                        <SuperIcon size={16} className={meta.color} />
                      </div>
                      <span className="text-sm font-semibold text-gray-800 leading-tight">{parentCat.label}</span>
                      <div className="flex items-center gap-1 text-xs text-gray-400">
                        <span>{parentCat.services.length} trades</span>
                        <ChevronRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}